import type { InstanceContact, InstanceDetail, NodeAssignmentView } from "@keydials/api-client";

/**
 * Allocation (KEY-916), client side: who has a node, and whether the viewer is them. The server keeps the lock —
 * while a node is allocated only its assignee and dial admins move its status — and these let the app say so up
 * front instead of bouncing off a 403.
 */

export interface NodeScope {
  sectionKey: string;
  segmentKey?: string | null;
  itemKey?: string | null;
}

const blank = (v: string | null | undefined) => (v ? v : null);

/** The live allocation on exactly this node, if any. */
export function assignmentAt(detail: Pick<InstanceDetail, "assignments">, scope: NodeScope): NodeAssignmentView | null {
  const seg = blank(scope.segmentKey);
  const item = seg ? blank(scope.itemKey) : null;
  return (detail.assignments ?? []).find((a) =>
    a.state === "Active" && a.sectionKey === scope.sectionKey && blank(a.segmentKey) === seg && blank(a.itemKey) === item) ?? null;
}

/** The live allocation that holds this node — its own, or one on a node above it (a section's covers its action
 * points and items; an action point's covers its items). The nearest wins. */
export function coveringAssignment(detail: Pick<InstanceDetail, "assignments">, scope: NodeScope): NodeAssignmentView | null {
  const seg = blank(scope.segmentKey);
  const item = seg ? blank(scope.itemKey) : null;
  const covering = (detail.assignments ?? []).filter((a) =>
    a.state === "Active" && a.sectionKey === scope.sectionKey
    && (!a.segmentKey || (a.segmentKey === seg && (!a.itemKey || a.itemKey === item))));
  const depth = (a: NodeAssignmentView) => (a.itemKey ? 2 : a.segmentKey ? 1 : 0);
  return covering.sort((x, y) => depth(y) - depth(x))[0] ?? null;
}

export interface AllocationViewer {
  userId: string | null;
  email: string | null;
  isGuest: boolean;
}

/** Whether the viewer is the allocated person: the member themselves, or the contact's guest account (matched by
 * email, as the server does). */
export function isAssignee(
  assignment: NodeAssignmentView,
  viewer: AllocationViewer,
  contacts: readonly Pick<InstanceContact, "id" | "email">[],
): boolean {
  if (assignment.assigneeKind === "Member") {
    return !!viewer.userId && assignment.assigneeId.toLowerCase() === viewer.userId.toLowerCase();
  }
  const email = contacts.find((c) => c.id.toLowerCase() === assignment.assigneeId.toLowerCase())?.email;
  return viewer.isGuest && !!email && !!viewer.email && email.trim().toLowerCase() === viewer.email.trim().toLowerCase();
}

/** "4 Nov" — the due date as the badge shows it. */
export function dueLabel(dueDate: string): string {
  const d = new Date(dueDate.length === 10 ? `${dueDate}T00:00:00Z` : dueDate);
  return Number.isNaN(d.getTime()) ? dueDate : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

/** "Allocated to Sam · due 4 Nov". */
export function allocationLabel(assignment: NodeAssignmentView): string {
  return `Allocated to ${assignment.assigneeName ?? "someone no longer on this dial"} · due ${dueLabel(assignment.dueDate)}`;
}

/** Why a status can't be moved here, when someone else has the node. */
export function lockReason(assignment: NodeAssignmentView): string {
  return `${allocationLabel(assignment)} — only they or a dial admin can change this.`;
}
